'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Link } from '@/i18n/routing';
import styles from './ProductPreview.module.css';

interface Issue {
  severity: 'high' | 'medium' | 'low';
  key: string;
}

// Issue keys that map to translation keys
const PREVIEW_ISSUE_KEYS: Issue[] = [
  { severity: 'high', key: 'cta' },
  { severity: 'high', key: 'hierarchy' },
  { severity: 'medium', key: 'form' },
  { severity: 'low', key: 'focus' },
];

const BLURRED_ISSUE_KEYS: Issue[] = [
  { severity: 'high', key: 'contrast' },
  { severity: 'medium', key: 'hover' },
];

function SeverityIndicator({ severity, label }: { severity: 'high' | 'medium' | 'low'; label: string }) {
  const bars = [1, 2, 3, 4, 5];
  const filledCount = severity === 'high' ? 5 : severity === 'medium' ? 3 : 1;

  return (
    <div className={`${styles.severityIndicator} ${styles[severity]}`}>
      <div className={styles.severityBars}>
        {bars.map((i) => (
          <span
            key={i}
            className={`${styles.severityBar} ${i <= filledCount ? styles.filled : ''}`}
          />
        ))}
      </div>
      <span className={styles.severityLabel}>{label}</span>
    </div>
  );
}

export function ProductPreview() {
  const t = useTranslations('audit.productPreview');
  const previewRef = useRef<HTMLDivElement>(null);
  const issuesRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleIssues, setVisibleIssues] = useState<number[]>([]);

  // 3D flip intro animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Scroll-based reveal for issues
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            if (!isNaN(index)) {
              setVisibleIssues((prev) =>
                prev.includes(index) ? prev : [...prev, index]
              );
            }
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    const issues = issuesRef.current?.querySelectorAll('[data-index]');
    issues?.forEach((issue) => observer.observe(issue));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={previewRef}
      className={`${styles.preview} ${isVisible ? styles.flipped : ''}`}
    >
      <div className={styles.previewWindow}>
        {/* Browser Chrome */}
        <div className={styles.previewBar}>
          <div className={styles.previewDot} />
          <div className={styles.previewDot} />
          <div className={styles.previewDot} />
        </div>

        {/* Content */}
        <div className={styles.previewContent}>
          <div className={`${styles.previewHeader} ${isVisible ? styles.fadeIn : ''}`}>
            <span className={styles.previewTitle}>{t('title')}</span>
            <span className={styles.previewBadge}>{t('issuesFound')}</span>
          </div>

          <div ref={issuesRef} className={styles.previewIssues}>
            {PREVIEW_ISSUE_KEYS.map((issue, index) => (
              <div
                key={issue.key}
                data-index={index}
                className={`${styles.previewIssue} ${visibleIssues.includes(index) ? styles.slideIn : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <SeverityIndicator
                  severity={issue.severity}
                  label={t(`severity.${issue.severity}`)}
                />
                <div className={styles.issueContent}>
                  <div className={styles.issueTitle}>{t(`findings.${issue.key}.title`)}</div>
                  <div className={styles.issueDesc}>{t(`findings.${issue.key}.description`)}</div>
                  <div className={styles.issuePrinciple}>{t(`findings.${issue.key}.principle`)}</div>
                </div>
              </div>
            ))}

            {/* Teaser Section (Blurred) */}
            <div
              data-index={PREVIEW_ISSUE_KEYS.length}
              className={`${styles.previewTeaser} ${visibleIssues.includes(PREVIEW_ISSUE_KEYS.length) ? styles.slideIn : ''}`}
              style={{ transitionDelay: `${PREVIEW_ISSUE_KEYS.length * 100}ms` }}
            >
              <div className={styles.teaserBlur}>
                {BLURRED_ISSUE_KEYS.map((issue) => (
                  <div key={issue.key} className={`${styles.previewIssue} ${styles.blurred}`}>
                    <SeverityIndicator
                      severity={issue.severity}
                      label={t(`severity.${issue.severity}`)}
                    />
                    <div className={styles.issueContent}>
                      <div className={styles.issueTitle}>{t(`findings.${issue.key}.title`)}</div>
                      <div className={styles.issueDesc}>{t(`findings.${issue.key}.description`)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.teaserOverlay}>
                <div className={styles.teaserBadge}>{t('moreIssues')}</div>
                <p className={styles.teaserText}>{t('unlockText')}</p>
                <Link href="/demo-report">
                  <Button size="md">{t('viewDemoReport')}</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
